import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { api, mediaUrl } from '@/lib/api';
import { ConfigurableHero } from '@/components/ConfigurableHero';
import { ChannelTile } from '@/components/ChannelTile';
import { MarketingPopup, MarketingHeroCard } from '@/components/Marketing';
import HomeSections from '@/components/HomeSections';
import HomepageCollections from '@/components/HomepageCollections';

// Strip HTML tags for short text previews (line-clamped cards) so TipTap
// authored HTML doesn't show markup in tight spaces.
function stripHtml(input) {
  if (!input) return '';
  const s = String(input);
  // If there are no tags, return as-is; otherwise strip them.
  if (!/[<>]/.test(s)) return s;
  return s.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

const FALLBACK_CHANNELS = [
  { route: '/watch', label: 'Watch', accent: 'pink', description: 'Videos, interviews, podcast clips, visual stories.', cta_label: 'Start watching',
    image_url: 'https://images.unsplash.com/photo-1485846234645-a62644f84728?w=1400&q=82&fm=jpg', show_in_carousel: true },
  { route: '/music', label: 'Music', accent: 'emerald', description: 'Tracks, albums, singles, and audio reflections.', cta_label: 'Start listening',
    image_url: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=1400&q=82&fm=jpg', show_in_carousel: true },
  { route: '/podcasts', label: 'Podcasts', accent: 'emerald', description: 'Episodes, conversations, long-form thinking.', cta_label: 'Subscribe',
    image_url: 'https://images.unsplash.com/photo-1590602847861-f357a9332bbc?w=1400&q=82&fm=jpg', show_in_carousel: true },
  { route: '/on-my-mind', label: 'On My Mind', accent: 'gold', description: 'Thoughts, articles, reflections, daily drops.', cta_label: 'Start reading',
    image_url: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?w=1400&q=82&fm=jpg', show_in_carousel: true },
];

function RecentTicker({ items }) {
  if (!items || items.length === 0) return null;
  const labelFor = (k) => ({ audio: 'MUSIC/PODCAST', article: 'ON MY MIND', video: 'WATCH', product: 'VAULT' })[k] || k.toUpperCase();
  const doubled = [...items, ...items];
  return (
    <div className="border-y border-[var(--pg-border-2)] bg-[rgba(7,8,15,0.55)] backdrop-blur-sm py-3">
      <div className="pg-marquee"><div className="pg-marquee-track">
        {doubled.map((r, i) => (
          <div key={i} className="flex items-center gap-3 px-3">
            <span className="font-display text-xs tracking-[0.32em] pg-text-glow-emerald">{labelFor(r.kind)}</span>
            <span className="font-mono text-[10px] text-[var(--pg-ink-faint)]">·</span>
            <span className="text-sm text-[var(--pg-ink)] truncate max-w-[280px]">{r.item?.title}</span>
            <span className="font-mono text-[10px] text-[var(--pg-ink-faint)] tracking-widest">NEW DROP</span>
          </div>
        ))}
      </div></div>
    </div>
  );
}

function ZoneHeading({ kicker, title, accent = 'emerald' }) {
  const glow = accent === 'pink' ? 'pg-text-glow-pink' : accent === 'gold' ? 'pg-text-glow-gold' : 'pg-text-glow-emerald';
  return (
    <div className="mb-6 relative z-10">
      <span className="inline-block bg-[rgba(10,8,16,0.5)] rounded-md px-2.5 py-1">
        <p className={`font-mono text-[11px] tracking-[0.32em] ${glow}`} style={{ textShadow: '0 2px 14px rgba(0,0,0,0.85), 0 1px 2px rgba(0,0,0,0.9)' }}>{kicker}</p>
      </span>
      <div className="mt-2">
        <span className="inline-block bg-[rgba(10,8,16,0.5)] rounded-md px-3 py-1.5">
          <h2 className="font-display text-2xl sm:text-3xl text-[var(--pg-ink)] tracking-[0.06em]" style={{ textShadow: '0 2px 18px rgba(0,0,0,0.9), 0 1px 2px rgba(0,0,0,0.95)' }}>{title}</h2>
        </span>
      </div>
    </div>
  );
}

export default function HomePage({ heroOverride }) {
  const [feed, setFeed] = useState(null);
  const [hasCollections, setHasCollections] = useState(false);
  const [audio, setAudio] = useState([]);
  const [pods, setPods] = useState([]);
  const [articles, setArticles] = useState([]);
  const [products, setProducts] = useState([]);
  const [channels, setChannels] = useState(FALLBACK_CHANNELS);
  const [settings, setSettings] = useState(null);
  const [marketing, setMarketing] = useState({ popup: null, hero: null, popupDismissed: false });

  useEffect(() => {
    api.get('/home/feed').then(({ data }) => setFeed(data)).catch(() => setFeed({}));
    api.get('/audio?limit=6').then(({ data }) => {
      setAudio(data.filter((a) => a.kind === 'music').slice(0, 4));
      setPods(data.filter((a) => a.kind === 'podcast').slice(0, 4));
    }).catch(() => {});
    api.get('/articles?limit=4').then(({ data }) => setArticles(data)).catch(() => {});
    api.get('/products?limit=4').then(({ data }) => setProducts(data)).catch(() => {});
    api.get('/channels').then(({ data }) => {
      if (Array.isArray(data) && data.length > 0) {
        setChannels(data);
      }
    }).catch(() => {});
    api.get('/settings').then(({ data }) => setSettings(data)).catch(() => {});
    api.get('/marketing/active').then(({ data }) => {
      const list = Array.isArray(data) ? data : [];
      setMarketing({
        popup: list.find((p) => p.placement === 'popup') || null,
        hero: list.find((p) => p.placement === 'home_hero') || null,
        popupDismissed: false,
      });
    }).catch(() => {});
  }, []);

  const carouselChannels = channels.filter((c) => c.show_in_carousel !== false);
  const collabTypoEnabled = settings ? settings.collab_typography_enabled !== false : true;
  const collabTypoText = settings?.collab_typography_text || 'COLLAB WITH G';

  return (
    <div>
      {/* Zone 01: Hero — configurable via Homepage Director. Preview pages
          can override by passing `heroOverride` (a rendered ReactNode). */}
      {heroOverride ? heroOverride : <ConfigurableHero />}

      {/* Homepage Collections (Homepage Director → Collections) — renders only when configured. */}
      <HomepageCollections onLoaded={(count) => setHasCollections(count > 0)} />

      {/* Recently added marquee — Zone 04 inspiration */}
      <RecentTicker items={feed?.recently_added || []} />

      {/* Marketing — inline hero card (CMS-controlled, optional) */}
      {marketing.hero && <MarketingHeroCard poster={marketing.hero} />}

      {/* Zone 02: Primary Channel Navigation (DB-driven carousel) */}
      <section id="channels" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="inline-block bg-[rgba(10,8,16,0.5)] rounded-md px-2.5 py-1">
              <p className="pg-text-glow-emerald font-mono text-[11px] tracking-[0.32em]" style={{ textShadow: '0 2px 14px rgba(0,0,0,0.85), 0 1px 2px rgba(0,0,0,0.9)' }}>THE CHANNELS</p>
            </span>
            <div className="mt-2">
              <span className="inline-block bg-[rgba(10,8,16,0.5)] rounded-md px-3 py-1.5">
                <h2 className="font-display text-3xl sm:text-4xl text-[var(--pg-ink)] tracking-[0.06em]" style={{ textShadow: '0 2px 18px rgba(0,0,0,0.9), 0 1px 2px rgba(0,0,0,0.95)' }}>PICK YOUR FREQUENCY.</h2>
              </span>
            </div>
          </div>
          <p className="hidden sm:block text-xs text-[var(--pg-ink-faint)] font-mono tracking-widest">{String(carouselChannels.length).padStart(2,'0')} DESTINATIONS</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="channels-grid">
          {carouselChannels.map((c, i) => (
            <ChannelTile
              key={c.route}
              index={i}
              to={c.route}
              number={i + 1}
              label={c.label}
              description={c.description}
              cta={c.cta_label || 'Enter'}
              image={mediaUrl(c.image_url)}
              accent={c.accent}
            />
          ))}
        </div>

        {/* Floating "COLLAB WITH G" typography (per design spec) */}
        {collabTypoEnabled && (
          <motion.div
            initial={{ opacity: 1, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-16 sm:mt-20 flex justify-center"
            data-testid="collab-floating-typography"
          >
            <Link
              to="/collab"
              className="relative group inline-block"
              aria-label="Open Collab with G"
            >
              <span
                aria-hidden
                className="absolute inset-0 font-display tracking-[0.04em] text-center pointer-events-none"
                style={{
                  fontSize: 'clamp(2.5rem, 8vw, 7rem)',
                  lineHeight: 1,
                  color: 'transparent',
                  WebkitTextStroke: '1px var(--pg-gold-2)',
                  filter: 'blur(2px)',
                  opacity: 0.22,
                  transform: 'translate(6px, 6px)',
                }}
              >
                {collabTypoText}
              </span>
              <span
                className="relative font-display tracking-[0.04em] text-center block transition-transform duration-300 group-hover:-translate-y-1"
                style={{
                  fontSize: 'clamp(2.5rem, 8vw, 7rem)',
                  lineHeight: 1,
                  color: 'var(--pg-gold-2)',
                  WebkitTextStroke: '1.5px var(--pg-gold-2)',
                  textShadow: '0 2px 10px rgba(0,0,0,0.85), 0 1px 3px rgba(0,0,0,0.9), 0 0 40px rgba(226,199,122,0.3)',
                }}
              >
                {collabTypoText}
              </span>
              <span className="block mt-3 text-center font-mono text-[10px] tracking-[0.32em] pg-text-glow-gold opacity-100 transition-opacity">
                OPEN THE ROOM →
              </span>
            </Link>
          </motion.div>
        )}
      </section>

      {/* Zone 02.5+ Fallback content zones — only render when the Homepage Director
          has NO collections configured. When collections exist, <HomepageCollections />
          (rendered elsewhere) is the single source of truth so the admin's visibility
          toggles are respected. */}
      {!hasCollections && (
        <>
          <HomeSections />

          {/* Zone 03: Currently Being Served */}
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-12">
            <ZoneHeading kicker="CURRENTLY BEING SERVED" title="ON THE SHOW TODAY." accent="pink" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="pg-card p-6" style={{ borderColor: 'rgba(255,62,165,0.20)' }}>
                <p className="pg-text-glow-pink font-mono text-[10px] tracking-[0.32em]">WHAT'S ON G'S MIND</p>
                <p className="text-[var(--pg-ink)] mt-2 font-serif text-lg">{feed?.on_my_mind?.title || 'A signal, not noise.'}</p>
                <p className="text-[var(--pg-ink)] text-sm mt-2 leading-relaxed whitespace-pre-wrap line-clamp-4">{stripHtml(feed?.on_my_mind?.body) || 'Quality over quantity. Things worth keeping.'}</p>
              </div>
              <Link to={feed?.latest_observation?.slug ? `/on-my-mind/${feed.latest_observation.slug}` : '/on-my-mind'} className="pg-card pg-card-hover p-6 block" style={{ borderColor: 'rgba(226,199,122,0.22)' }}>
                <p className="pg-text-glow-gold font-mono text-[10px] tracking-[0.32em]">LATEST OBSERVATION</p>
                <p className="text-[var(--pg-ink)] mt-2 font-serif text-lg line-clamp-2">{feed?.latest_observation?.title || 'The pen is on the desk.'}</p>
                <p className="text-[var(--pg-ink)] text-sm mt-2 leading-relaxed line-clamp-3">{feed?.latest_observation?.excerpt || feed?.latest_observation?.subtitle || 'The first essay is on its way.'}</p>
                <p className="text-xs font-mono tracking-[0.24em] pg-text-glow-gold mt-4">{feed?.latest_observation ? 'READ →' : 'VISIT ON MY MIND →'}</p>
              </Link>
              <Link to={feed?.new_arrival ? `/vault/${feed.new_arrival.id}` : '/vault'} className="pg-card pg-card-hover p-6 block" style={{ borderColor: 'rgba(255,62,165,0.22)' }}>
                <p className="pg-text-glow-pink font-mono text-[10px] tracking-[0.32em]">NEW IN VAULT</p>
                <p className="text-[var(--pg-ink)] mt-2 font-serif text-lg line-clamp-2">{feed?.new_arrival?.title || 'The shelves are being stocked.'}</p>
                <p className="text-[var(--pg-ink)] text-sm mt-2 leading-relaxed line-clamp-3">{feed?.new_arrival?.description?.slice?.(0, 180) || 'Guides, workbooks, and audio pieces.'}</p>
                <p className="text-xs font-mono tracking-[0.24em] pg-text-glow-pink mt-4">{feed?.new_arrival ? 'GET IT →' : 'VISIT THE VAULT →'}</p>
              </Link>
            </div>
          </section>

          {/* Zone 06: Latest Music */}
          {audio.length > 0 && (
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-12">
              <ZoneHeading kicker="LATEST MUSIC" title="FROM THE DECK." accent="emerald" />
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {audio.map((a, i) => (
                  <Link key={a.id} to="/music" className="pg-card pg-card-hover overflow-hidden block group">
                    <div className="aspect-square bg-[var(--pg-navy-800)]">{a.cover_url ? <img src={mediaUrl(a.cover_url)} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full flex items-center justify-center text-[var(--pg-emerald)] text-3xl">♪</div>}</div>
                    <div className="p-3">
                      <h4 className="font-serif text-sm text-[var(--pg-ink)] line-clamp-2">{a.title}</h4>
                      <p className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] mt-1 uppercase">{a.kind}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Zone 07: Featured Podcasts */}
          {pods.length > 0 && (
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-12">
              <ZoneHeading kicker="FEATURED PODCASTS" title="LATEST EPISODES." accent="emerald" />
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {pods.map((p) => (
                  <Link key={p.id} to="/podcasts" className="pg-card pg-card-hover overflow-hidden block group">
                    <div className="aspect-square bg-[var(--pg-navy-800)]">{p.cover_url ? <img src={mediaUrl(p.cover_url)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full flex items-center justify-center text-[var(--pg-emerald)] text-3xl">♬</div>}</div>
                    <div className="p-3">
                      <h4 className="font-serif text-sm text-[var(--pg-ink)] line-clamp-2">{p.title}</h4>
                      <p className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] mt-1 uppercase">Episode</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Zone 05: New in Vault */}
          {products.length > 0 && (
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-12">
              <ZoneHeading kicker="NEW IN VAULT" title="DIGITAL DROPS." accent="pink" />
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {products.map((p) => (
                  <Link key={p.id} to={`/vault/${p.id}`} className="pg-card pg-card-hover overflow-hidden block group" style={{ borderColor: 'rgba(255,62,165,0.20)' }}>
                    <div className="aspect-[4/5] bg-[var(--pg-navy-800)]">{(p.primary_image_url || p.cover_url) ? <img src={mediaUrl(p.primary_image_url || p.cover_url)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full flex items-center justify-center text-[var(--pg-ink-faint)]">◆</div>}</div>
                    <div className="p-3">
                      <h4 className="font-serif text-sm text-[var(--pg-ink)] line-clamp-2">{p.title}</h4>
                      <p className="text-xs font-mono pg-text-glow-pink mt-1">${(p.price || 0).toFixed(2)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Zone 08: On My Mind */}
          {articles.length > 0 && (
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-16">
              <ZoneHeading kicker="ON MY MIND" title="THE LATEST WRITING." accent="gold" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {articles.map((a) => (
                  <Link key={a.id} to={`/on-my-mind/${a.slug}`} className="pg-card pg-card-hover block p-4" style={{ borderColor: 'rgba(226,199,122,0.20)' }}>
                    <p className="pg-text-glow-gold font-mono text-[10px] tracking-[0.32em]">{(a.tags?.[0] || 'ESSAY').toUpperCase()}</p>
                    <h3 className="font-serif text-base text-[var(--pg-ink)] mt-2 line-clamp-3">{a.title}</h3>
                    <p className="mt-3 text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)]">{a.read_time || ''}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {/* Zone 09: Future Expansion (placeholder banner) — ALWAYS visible */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-20">
        <div className="pg-card p-8 sm:p-10 text-center" style={{ borderColor: 'rgba(226,199,122,0.18)' }}>
          <p className="font-mono text-[11px] tracking-[0.32em] pg-text-glow-gold">SOMETHING IS COMING</p>
          <h3 className="font-display text-2xl sm:text-3xl mt-3 tracking-[0.06em]" style={{ textShadow: '0 1px 8px rgba(0,0,0,0.85)' }}>SUBSCRIBE TO THE SIGNAL.</h3>
          <p className="text-[var(--pg-ink)] mt-3 max-w-md mx-auto">When new drops land — music, podcasts, exclusives — you'll be the first to know.</p>
        </div>
      </section>

      {/* Marketing popup — 5s delay, dismissible, respects frequency */}
      {marketing.popup && !marketing.popupDismissed && (
        <MarketingPopup
          poster={marketing.popup}
          autoShowDelayMs={5000}
          onDismiss={() => setMarketing((m) => ({ ...m, popupDismissed: true }))}
        />
      )}
    </div>
  );
}
